// AssetLoader: runs a batch of loading tasks in parallel and emits
// progress/complete events.
//
// Task shape:
//   { type: 'fake',  key, delayMs }   — loading-screen padding (Phase 2 demo)
//   { type: 'gltf',  key, url }       — three GLTFLoader; missing files fail soft
//   { type: 'image', key, url }       — future
//   { type: 'audio', key, url }       — future
//
// Events:
//   'progress' (loaded, total)
//   'complete' ()
//
// Soft-fail policy: a missing GLTF (404) resolves the task with `null`
// instead of rejecting the whole batch — game starts fine with optional
// assets absent. Subscribers check `assets.<key>` for null before using.

import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

const EVENTS = ['progress', 'complete'];

export class AssetLoader {
	constructor() {
		this._tasks = [];
		this._listeners = Object.fromEntries(EVENTS.map((e) => [e, []]));
		this.loaded = 0;
		this.total = 0;
		this.assets = Object.create(null);
		this._gltfLoader = null;
	}

	on(event, callback) {
		if (!this._listeners[event]) {
			throw new Error(`AssetLoader: unknown event "${event}"`);
		}
		this._listeners[event].push(callback);
		return this;
	}

	// Register a "fake" loading task — sleeps for `delayMs` then resolves.
	// Useful for loading-screen padding before real assets exist.
	addFake(key, delayMs) {
		this._tasks.push({ type: 'fake', key, delayMs });
		this.total++;
		return this;
	}

	// Register a GLTF (.glb/.gltf) load. Soft-fails to null on 404 or parse
	// error — caller is responsible for checking before use.
	addGLTF(key, url) {
		this._tasks.push({ type: 'gltf', key, url });
		this.total++;
		return this;
	}

	// Kick off all registered tasks in parallel. Returns a Promise that
	// resolves when every task finishes. 'progress' fires after each task,
	// 'complete' fires once everything is done.
	load() {
		if (this._tasks.length === 0) {
			this._emit('complete');
			return Promise.resolve();
		}
		return Promise.all(this._tasks.map((task) => this._runTask(task)))
			.then(() => this._emit('complete'));
	}

	_runTask(task) {
		switch (task.type) {
			case 'fake':
				return new Promise((resolve) => {
					setTimeout(() => {
						this.assets[task.key] = true;
						this._tick();
						resolve();
					}, task.delayMs);
				});
			case 'gltf':
				return new Promise((resolve) => {
					if (!this._gltfLoader) this._gltfLoader = new GLTFLoader();
					this._gltfLoader.load(
						task.url,
						(gltf) => {
							this.assets[task.key] = gltf;
							this._tick();
							resolve();
						},
						undefined, // per-file progress unused
						(err) => {
							console.warn(`AssetLoader: GLTF "${task.url}" failed — continuing without it.`, err);
							this.assets[task.key] = null;
							this._tick();
							resolve();
						},
					);
				});
			default:
				return Promise.reject(new Error(`AssetLoader: unknown task type "${task.type}"`));
		}
	}

	_tick() {
		this.loaded++;
		this._emit('progress', this.loaded, this.total);
	}

	_emit(event, ...args) {
		for (const cb of this._listeners[event]) cb(...args);
	}
}
